import BackgroundJobs from 'components/backgroundjobs/backgroundJobs';
import { ProgressCircle } from 'components/customComponents/Controls/ProgressCircle';
import { SystemTask } from 'components/systemTasks/SystemTask';
import connectionHubManager from 'connectionHub/connectionHubManager';
import RMHub from 'connectionHub/RMHub';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { useGetBackgroundJobs } from 'redux/hooks/backgroundJobs';
import { removeCompletedBackgroundJob } from 'redux/reducers/backgroundJobsReducer';
import { removeCompletedTask, updateProgress } from 'redux/reducers/systemTaskReducer';
import { selectedSystemTasksState } from 'redux/selectors/StateSelectors';
import { IStyle } from 'Styles';
import './NotificationPanel.scss';

export default function NotificationsPanel() {
  const [showPanel, setShowPanel] = useState(false);
  const ref = useRef<HTMLInputElement>();
  const [connection, setConnection] = useState<RMHub | null>(null);
  const systemTaskSelector = useRMSelector((state) => selectedSystemTasksState(state));
  const backgroundJobs = useGetBackgroundJobs();
  const dispatch = useRMDispatch();

  useEffect(() => {
    const initHubConnection = async () => {
      const connection = await connectionHubManager.getConnection(
        'systemtaskhub',
        '/systemtaskhub',
      );
      setConnection(connection);
    };
    initHubConnection();
  }, []);

  const { progress, count } = useMemo(() => {
    let totalProgress = 0;
    let completedCount = 0;
    systemTaskSelector.forEach((t) => {
      totalProgress += t.progress;
      if (t.progress == 100) {
        completedCount++;
      }
    });

    backgroundJobs.forEach((t) => {
      totalProgress += t.progress;
      if (t.progress == 100) {
        completedCount++;
      }
    });

    return {
      progress: totalProgress / (systemTaskSelector.length + backgroundJobs.length),
      count: completedCount,
    };
  }, [systemTaskSelector, backgroundJobs]);

  useEffect(() => {
    const handleProgress = (progress, taskId) => {
      dispatch(updateProgress({ taskId, progress }));
    };
    if (connection) {
      connection.on('UpdateProgress', handleProgress);
    }
    return () => {
      connection?.off('UpdateProgress', handleProgress);
    };
  }, [connection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const current = ref.current;
      if (current != null && current != undefined) {
        if (!current?.contains(event.target)) {
          setShowPanel(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [ref]);

  const panelStyle: IStyle = useMemo(() => {
    return {
      display: showPanel == false ? 'none' : 'flex',
    } as IStyle;
  }, [showPanel]);

  const clearAllHandler = (e) => {
    dispatch(removeCompletedBackgroundJob());
    dispatch(removeCompletedTask());
  };

  return (
    <div
      id='notificationpanel-wrapper'
      ref={ref}
      style={styles.btnContainer}
      className="d-flex flex-row align-items-center"
    >
      <div id='notificationpanel-sidebar-showpanel'
        className="sidebar-item h-100 w-100"
        onClick={() => {
          setShowPanel(!showPanel);
        }}
      >
        <ProgressCircle progress={progress} count={count} />
        <div id='notificationpanel-user-menu-button-arrow' className="user-menu-button-arrow"></div>
      </div>
      <div id='notificationpanel-overflow-hidden' style={panelStyle} className={'notification-panel overflow-hidden'}>
        <div id='notificationpanel-header' style={styles.headerContainer}>Notification Panel</div>

        <div id='notificationpanel-border-line' className="user-panel-border-line" />
        <div id='notificationpanel-container-overflow-auto' className="notification-panel-container overflow-y-auto">
          {(count > 0 || backgroundJobs?.length) && (
            <div id='notificationpanel-clear-all-button' className="clearAllBtn" onClick={clearAllHandler}>
              Clear all
            </div>
          )}
          <BackgroundJobs />
          {systemTaskSelector.map((task) => {
            return <SystemTask id={`notificationpanel-backgroundjobs-${task.id}`} key={task.id} {...task} />;
          })}
          {systemTaskSelector.length === 0 &&
            backgroundJobs?.filter((item) => item.progress !== 100).length === 0 && (
              <div id='notificationpanel-empty-message' className="emptyPanel">Nothing to show here</div>
            )}
        </div>
      </div>
    </div>
  );
}

const styles: IStyle = {
  btnContainer: {
    height: '8%',
    marginTop: '1rem',
  },
  headerContainer: {
    margin: '1rem',
    height: '17.84%',
    width: '100%',
    color: 'black',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '16px',
  },
};
